/**
 * ADR-118 — cross-tab auth state sync via `storage` event.
 *
 * Logging in or out in tab A should propagate to tab B without
 * waiting for a 401 round-trip. The browser fires a `storage` event
 * on every OTHER tab/window of the same origin whenever localStorage
 * changes. We listen for changes to the lib's known keys and emit a
 * normalized event consumers (`libs/auth-identity`'s provider) can
 * subscribe to.
 *
 * The current tab does NOT receive its own `storage` events — that's
 * a deliberate browser feature, since the writing tab already knows
 * what it just wrote.
 */

import { StorageKeys, type StorageKey } from './storage';

/**
 * Cross-tab event kinds. Each maps to a state-machine action the
 * consuming provider will dispatch.
 */
export type CrossTabEventKind = 'login' | 'logout' | 'token-changed' | 'unknown';

export interface CrossTabEvent {
  kind: CrossTabEventKind;
  /** The raw storage key whose change triggered this event. */
  key: StorageKey;
  /**
   * New value (post-write). May be `null` if the key was removed.
   * Stringified shape; consumers JSON.parse if they need to look
   * inside (e.g. read the new user id).
   */
  newValue: string | null;
}

export type CrossTabListener = (event: CrossTabEvent) => void;

/**
 * Subscribe to cross-tab auth state changes. Returns an unsubscribe
 * function the caller MUST invoke on component unmount to avoid
 * leaks.
 *
 * Example:
 *
 *   useEffect(() => {
 *     return subscribeCrossTab((event) => {
 *       if (event.kind === 'logout') dispatch({ type: 'logout' });
 *       if (event.kind === 'login') {
 *         // re-hydrate from storage
 *       }
 *     });
 *   }, [dispatch]);
 */
export function subscribeCrossTab(listener: CrossTabListener): () => void {
  if (typeof window === 'undefined') {
    // SSR / test env without window — no-op.
    return () => {
      /* no-op */
    };
  }

  const handler = (event: StorageEvent) => {
    const key = event.key as StorageKey | null;
    if (!key || !isKnownAuthKey(key)) {
      return;
    }
    listener({
      kind: classifyEvent(key, event.oldValue, event.newValue),
      key,
      newValue: event.newValue,
    });
  };

  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

function isKnownAuthKey(key: string): key is StorageKey {
  return (Object.values(StorageKeys) as string[]).includes(key);
}

/**
 * Classify a `storage` event into a coarse auth-state kind.
 *
 *   token removed → logout
 *   token added (was empty) → login
 *   token changed (both old + new present, different) → token-changed (refresh)
 *
 * Non-token key changes (permissions, heartbeat) classify as
 * `token-changed` so consumers know to re-read storage but can
 * decide whether they care.
 */
function classifyEvent(
  key: StorageKey,
  oldValue: string | null,
  newValue: string | null,
): CrossTabEventKind {
  if (key !== StorageKeys.token) {
    // Non-token change — let consumers decide; treat as token-changed
    // signal so they re-hydrate if they care.
    if (key === StorageKeys.user && newValue === null) {
      // user cleared without token clear is unusual but treat as logout
      return 'logout';
    }
    return 'token-changed';
  }
  if (newValue === null) return 'logout';
  if (oldValue === null) return 'login';
  if (oldValue !== newValue) return 'token-changed';
  return 'unknown';
}

/**
 * Bump the heartbeat key. Writing a fresh value triggers `storage`
 * events in other tabs. Use this when you want to *force* a
 * cross-tab refresh without changing the actual auth state — e.g.
 * after a permissions_version bump that re-fetched the user.
 */
export function bumpHeartbeat(now: () => number = Date.now): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(StorageKeys.heartbeat, String(now()));
  } catch {
    // privacy mode, ignore
  }
}
